import React from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { OrderProduct } from './OrderTypes';
import { UPLOAD_BASE_URL } from '../../config';

interface ProductSpecsStepProps {
  selectedProduct: OrderProduct;
  options: Record<string, string | number>;
  errors: Record<string, string>;
  quantity: number;
  onOptionChange: (name: string, value: string | number) => void;
  onQuantityChange: (value: number) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>, side: 'front' | 'back') => void;
  designFile: File | null;
  uploadedFileName: string | null;
  backDesignFile?: File | null;
  backUploadedFileName?: string | null;
  onRemoveFile: (side: 'front' | 'back') => void;
  uploadError: string;
  isUploading: boolean;
  uploadProgress: number;
  onAddToCart: () => void;
  onBack: () => void;
  orderPrice: number;
  onOpenSavedDesigns: (side: 'front' | 'back') => void;
}

const ProductSpecsStep: React.FC<ProductSpecsStepProps> = ({
  selectedProduct,
  options,
  errors,
  quantity,
  onOptionChange,
  onQuantityChange,
  onFileChange,
  designFile,
  uploadedFileName,
  backDesignFile = null,
  backUploadedFileName = null,
  onRemoveFile,
  uploadError,
  isUploading,
  uploadProgress,
  onAddToCart,
  onBack,
  orderPrice,
  onOpenSavedDesigns,
}) => {
  const [frontPreviewUrl, setFrontPreviewUrl] = React.useState<string | null>(null);
  const [backPreviewUrl, setBackPreviewUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (designFile) {
      if (designFile.type.startsWith('image/')) {
        const objectUrl = URL.createObjectURL(designFile);
        setFrontPreviewUrl(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
      } else {
        setFrontPreviewUrl(null);
      }
    } else if (uploadedFileName && uploadedFileName.match(/\.(jpeg|jpg|gif|png|webp)$/i)) {
      setFrontPreviewUrl(`${UPLOAD_BASE_URL}/${uploadedFileName}`);
    } else {
      setFrontPreviewUrl(null);
    }
  }, [designFile, uploadedFileName]);

  React.useEffect(() => {
    if (backDesignFile) {
      if (backDesignFile.type.startsWith('image/')) {
        const objectUrl = URL.createObjectURL(backDesignFile);
        setBackPreviewUrl(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
      } else {
        setBackPreviewUrl(null);
      }
    } else if (backUploadedFileName && backUploadedFileName.match(/\.(jpeg|jpg|gif|png|webp)$/i)) {
      setBackPreviewUrl(`${UPLOAD_BASE_URL}/${backUploadedFileName}`);
    } else {
      setBackPreviewUrl(null);
    }
  }, [backDesignFile, backUploadedFileName]);

  const getFileIcon = (file: File) => {
    const name = file.name.toLowerCase();
    if (file.type.startsWith('image/')) return 'far fa-file-image text-success';
    if (file.type === 'application/pdf' || name.endsWith('.pdf')) return 'far fa-file-pdf text-danger';
    if (name.endsWith('.zip') || name.endsWith('.rar') || name.endsWith('.7z')) return 'far fa-file-archive text-warning';
    if (name.endsWith('.psd') || name.endsWith('.ai') || name.endsWith('.cdr')) return 'far fa-file-code text-info';
    return 'far fa-file-alt text-primary';
  };

  const isDoubleSided = Object.values(options).some(
    val => String(val).toLowerCase().includes('double sided') || String(val).toLowerCase() === 'double sided'
  );

  const showQuantityButtons = [
    'visiting-cards',
    'flyers',
    'brochures',
    'stickers-labels',
    'wedding-invites',
  ].includes(selectedProduct.id);

  const renderUploadZone = (
    label: string,
    file: File | null,
    uploadedName: string | null,
    side: 'front' | 'back',
    inputFieldId: string,
    preview: string | null
  ) => {
    const hasFile = file || uploadedName;
    return (
      <div className="mb-3">
        <label className="form-label fw-bold text-dark mb-2" style={{ fontSize: '13.5px' }}>
          {label}
        </label>
        <div className="design-upload-zone border-dashed border-2 rounded-4 p-4 text-center bg-white cursor-pointer transition">
          {!hasFile ? (
            <div>
              <i className="fas fa-cloud-upload-alt text-primary mb-2" style={{ fontSize: '32px' }}></i>
              <p className="mb-1 text-dark fw-semibold" style={{ fontSize: '13px' }}>
                Drag & drop your file here or click to browse
              </p>
              <input
                type="file"
                id={inputFieldId}
                className="d-none"
                onChange={(e) => onFileChange(e, side)}
              />
              <div className="d-flex justify-content-center gap-2 mt-2">
                <label
                  htmlFor={inputFieldId}
                  className="btn btn-outline-primary btn-sm px-3 py-1 fw-semibold mb-0"
                  style={{ borderRadius: '8px', fontSize: '12px' }}
                >
                  <i className="fas fa-upload me-1"></i> Upload
                </label>
                {localStorage.getItem('token') && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm px-3 py-1 fw-semibold"
                    style={{ borderRadius: '8px', fontSize: '12px' }}
                    onClick={() => onOpenSavedDesigns(side)}
                  >
                    <i className="fas fa-images me-1"></i> My Saved Designs
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="d-flex flex-column align-items-center p-3 bg-light rounded-4 border border-light shadow-sm position-relative">
              {preview ? (
                <div className="mb-3 position-relative rounded-3 overflow-hidden border bg-white shadow-sm" style={{ maxWidth: '100%', maxHeight: '140px' }}>
                  <img
                    src={preview}
                    alt={`${label} Preview`}
                    style={{ objectFit: 'contain', width: '100%', maxHeight: '140px', display: 'block' }}
                  />
                </div>
              ) : (
                <div className="mb-3 d-flex align-items-center justify-content-center bg-white rounded-3 border shadow-sm animate-pulse" style={{ width: '70px', height: '70px' }}>
                  <i className={getFileIcon(file)} style={{ fontSize: '32px' }}></i>
                </div>
              )}
              
              <div className="w-100 d-flex align-items-center justify-content-between mt-2">
                <div className="d-flex align-items-center gap-2 text-start overflow-hidden me-2">
                  <i className={(file ? getFileIcon(file) : "far fa-image text-primary") + " flex-shrink-0"} style={{ fontSize: '14px' }}></i>
                  <div className="overflow-hidden">
                    <div className="fw-bold text-dark text-truncate" style={{ fontSize: '12px', maxWidth: '180px' }} title={file?.name || uploadedName || ''}>
                      {file?.name || uploadedName}
                    </div>
                    {file && (
                      <div className="text-muted" style={{ fontSize: '10px' }}>
                        {(file.size / 1024).toFixed(1)} KB
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm px-2.5 py-1 d-flex align-items-center gap-1.5"
                  onClick={() => onRemoveFile(side)}
                  style={{ borderRadius: '6px', fontSize: '11px', fontWeight: 600 }}
                >
                  <i className="fas fa-trash-alt"></i> Remove
                </button>
              </div>
            </div>
          )}
          {uploadError && <div className="text-danger mt-2" style={{ fontSize: '12px' }}>{uploadError}</div>}
          {isUploading && (
            <div className="progress mt-3" style={{ height: '8px' }}>
              <div className="progress-bar" role="progressbar" style={{ width: `${uploadProgress}%` }} aria-valuenow={uploadProgress} aria-valuemin={0} aria-valuemax={100}></div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="step-content-fade">
      <h4 className="fw-bold text-dark mb-2">Configure {selectedProduct.name}</h4>
      <p className="text-muted mb-4" style={{ fontSize: '13px' }}>
        Set options specific to this printing category.
      </p>

      <div className="options-container bg-light rounded-4 p-4 mb-4">
        {selectedProduct.options.map(opt => (
          <div key={opt.name} className="mb-4">
            <label className="form-label fw-bold text-dark mb-2" style={{ fontSize: '14px' }}>
              {opt.label}
            </label>

            {opt.type === 'select' && (
              <select
                className="form-select form-input py-2 border-light shadow-sm"
                value={options[opt.name] || ''}
                onChange={(e) => onOptionChange(opt.name, e.target.value)}
                style={{ borderRadius: '10px' }}
              >
                {opt.choices?.map(c => (
                  <option key={c.label} value={c.label}>
                    {c.label} {c.extraCost > 0 ? `(+₹${c.extraCost})` : ''}
                  </option>
                ))}
              </select>
            )}

            {opt.type === 'radio' && (
              <div className="d-flex flex-wrap gap-2 mt-1">
                {opt.choices?.map(c => {
                  const isSelected = options[opt.name] === c.label;
                  return (
                    <button
                      key={c.label}
                      type="button"
                      onClick={() => onOptionChange(opt.name, c.label)}
                      className={`btn py-2 px-3 text-start border transition ${isSelected ? 'btn-primary border-primary fw-semibold' : 'btn-outline-light bg-white border-light text-muted'}`}
                      style={{ borderRadius: '10px', fontSize: '13px', flex: '1 1 200px' }}
                    >
                      <i className={`far ${isSelected ? 'fa-dot-circle text-white' : 'fa-circle text-muted'} me-2`}></i>
                      {c.label} {c.extraCost > 0 ? `(+₹${c.extraCost})` : ''}
                    </button>
                  );
                })}
              </div>
            )}

            {opt.type === 'number' && (
              <div className="input-group shadow-sm rounded-3 overflow-hidden">
                <input
                  type="number"
                  className="form-control form-input border-0 py-2"
                  min={opt.min}
                  max={opt.max}
                  step={opt.step || 1}
                  value={options[opt.name] || ''}
                  onChange={(e) => onOptionChange(opt.name, Number(e.target.value))}
                  style={{ borderRadius: '10px 0 0 10px' }}
                />
                {opt.unit && (
                  <span className="input-group-text bg-white border-0 text-muted fw-semibold" style={{ borderRadius: '0 10px 10px 0', fontSize: '13px' }}>
                    {opt.unit}
                  </span>
                )}
              </div>
            )}

            {errors[opt.name] && (
              <div className="text-danger mt-1" style={{ fontSize: '12px' }}>{errors[opt.name]}</div>
            )}
          </div>
        ))}

        <div className="mb-4">
          <label className="form-label fw-bold text-dark mb-2" style={{ fontSize: '14px' }}>
            Order Quantity
          </label>
          
          <div className="d-flex flex-column gap-3">
            {showQuantityButtons && (
              <div className="d-flex flex-wrap gap-2">
                {([50, 100, 250, 500, 1000, 2000] as number[]).map(q => {
                  if (selectedProduct.id === 'visiting-cards' && q === 50) return null;
                  const isSelected = quantity === q;
                  return (
                    <button
                      key={q}
                      type="button"
                      onClick={() => onQuantityChange(q)}
                      className={`btn py-2 px-3 border transition ${isSelected ? 'btn-primary border-primary fw-semibold' : 'btn-outline-light bg-white border-light text-muted'}`}
                      style={{ borderRadius: '10px', fontSize: '13px', minWidth: '80px' }}
                    >
                      {q.toLocaleString()}
                    </button>
                  );
                })}
              </div>
            )}
            
            <div>
              {showQuantityButtons && (
                <div className="text-muted mb-2 fw-semibold" style={{ fontSize: '12px' }}>
                  Or enter custom quantity:
                </div>
              )}
              <div className="d-flex align-items-center gap-2" style={{ maxWidth: '200px' }}>
                <button
                  type="button"
                  className="btn btn-outline-secondary py-2 border-light bg-white rounded-3 d-flex align-items-center justify-content-center"
                  onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
                  style={{ width: '40px', height: '40px' }}
                >
                  <i className="fas fa-minus"></i>
                </button>
                <input
                  type="number"
                  className="form-control text-center py-2 border-light shadow-sm"
                  value={quantity || ''}
                  onChange={(e) => onQuantityChange(Math.max(1, Number(e.target.value)))}
                  style={{ borderRadius: '10px', height: '40px', fontSize: '14px', fontWeight: 'bold' }}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary py-2 border-light bg-white rounded-3 d-flex align-items-center justify-content-center"
                  onClick={() => onQuantityChange(quantity + 1)}
                  style={{ width: '40px', height: '40px' }}
                >
                  <i className="fas fa-plus"></i>
                </button>
              </div>
            </div>
          </div>
          
          {errors.quantity && (
            <div className="text-danger mt-1" style={{ fontSize: '12px' }}>{errors.quantity}</div>
          )}
        </div>

        {isDoubleSided ? (
          <Row className="g-3">
            <Col md={6}>
              {renderUploadZone("Front Side Design File (Optional)", designFile, uploadedFileName, 'front', 'front-design-file-input', frontPreviewUrl)}
            </Col>
            <Col md={6}>
              {renderUploadZone("Back Side Design File (Optional)", backDesignFile, backUploadedFileName, 'back', 'back-design-file-input', backPreviewUrl)}
            </Col>
          </Row>
        ) : (
          renderUploadZone("Upload Design File (Optional)", designFile, uploadedFileName, 'front', 'design-file-input', frontPreviewUrl)
        )}
      </div>

      {/* Mobile-only Price Subtotal */}
      <div className="d-block d-lg-none mt-3 p-3 bg-light border border-light rounded-4 text-center">
        <span className="text-muted d-block uppercase fw-bold mb-1" style={{ fontSize: '10.5px', letterSpacing: '0.5px' }}>
          Estimated Subtotal
        </span>
        <span className="fw-bold text-primary animate-fadeIn" style={{ fontSize: '24px' }}>
          ₹{orderPrice.toLocaleString('en-IN')}
        </span>
        <span className="text-muted d-block mt-0.5" style={{ fontSize: '11px' }}>
          *Includes GST and basic materials
        </span>
      </div>

      <div className="mt-4 pt-3 border-top d-flex flex-column flex-sm-row justify-content-between gap-3">
        <Button variant="outline-secondary" className="py-2.5" onClick={onBack}>
          <i className="fas fa-arrow-left me-1"></i> Back
        </Button>
        <Button variant="primary" className="py-2.5" onClick={onAddToCart}>
          Add to Cart <i className="fas fa-cart-plus ms-1"></i>
        </Button>
      </div>
    </div>
  );
};

export default ProductSpecsStep;
